import { Numeric } from "../Numeric";
import type { IJsonSchema, OpenAPIV2, OpenAPIV3 } from "../../@types";
import type { JsonSchemaProperty } from "./JsonSchemaProperty";
import type { InputEnumOption } from "../../ui/InputEnumOption";

/** Static utilities for JSON Schemas an Open API documents. */
export class JsonSchemaUtils {

  /**
   * Checks if the given object can be rendered/parsed as a table:
   * All properties are single values: type != "object".
   * NOTE: The function may later consider some known object types (e.g. Avatar, DateRange)
   * as single values without that being a breaking change.
   * @param schema The schema to evaluate.
   * @returns True if the schema can be rendered as a non-hierarchical table.
   */
  public static isTable(schema: OpenAPIV3.NonArraySchemaObject): boolean {
    if (!schema?.properties) {
      return false;
    }
    return Object.getOwnPropertyNames(schema.properties)
      .filter((key) => !key.startsWith("$"))
      .every((key) => (schema?.properties?.[key] && schema.properties[key] as OpenAPIV3.NonArraySchemaObject)?.type != "object" );
  }

  /**
   * Gets all the properties (the entire tree) from the schema
   * @param schema Schema from which to fetch the properties
   * @param prefix Prefix to add to property path: Mainly used in recursion.
   * @returns InputEnumOptions (typically used in UI) where value and text are
   * the property path, e.g. "owner.avatar.sortableName" and title shows the title or description from the schema.
   */
  public static getAllProperties(schema: OpenAPIV3.SchemaObject, prefix = ""): InputEnumOption[] {
    const result: InputEnumOption[] = [];
    Object.keys(schema.properties || {}).forEach(key => {
      const prop = schema.properties?.[key] as OpenAPIV3.SchemaObject;
      if (prop) {
        result.push({
          value: prefix + key,
          text: prefix + key,
          title: prop.title || prop.description,
        });
        if (prop.type == "object") {
          result.push(...this.getAllProperties(prop as OpenAPIV3.SchemaObject, prefix + key + "."));
        }
      }
    });
    return result;
  }

  /**
   * Guesses the schema based on a data object.
   * @param data A data object, e.g. the first line (after header) of incoming data.
   */
  public static guessSchema(data: { [key: string]: any }, typeResolve: "string" | "guess" = "guess"): OpenAPIV3.NonArraySchemaObject | undefined {
    if (!data) {
      return undefined;
    }
    const schema: OpenAPIV3.NonArraySchemaObject = {
      description: `Schema generated based on data: ${JSON.stringify(data)}.`,
      type: "object",
      format: "table",
      properties: {},
    };
    Object.keys(data).forEach((key) => {
      let type: OpenAPIV3.NonArraySchemaObjectType = "string";
      if (typeResolve == "guess") {
        const val: any = data[key];
        if (Numeric.isNumber(val)) {
          type = "number";
        }
        if (["true", "false"].indexOf(val) > -1) {
          type = "boolean";
        }
        // TODO: Add Date format and perhaps others.
      }
      schema.properties ??= {};
      schema.properties[key] = {
        type,
      };
    });
    return schema;
  }

  /**
   * Gets a property from a schema document. Supports single property names or longer property paths.
   * @param schema The root type from which the property is found.
   * @param path Property path starting with the root schema.
   */
  public static getProperty(schema: OpenAPIV3.SchemaObject, path: string): JsonSchemaProperty | undefined {
    if (!schema || !path?.trim()) {
      return undefined;
    }
    const pathArr = path.split(".");
    let member: OpenAPIV3.SchemaObject = schema;
    let parentName: string | undefined = undefined;
    let propertyName: string | undefined = undefined;
    let isRequired = false;
    while (pathArr.length) {
      propertyName = pathArr.shift()?.trim();
      if (!propertyName) {
        return undefined;
      }
      const memberOrRef = member.properties ? member.properties?.[propertyName] : null;
      parentName = member.format;
      isRequired = ((member.required?.indexOf(propertyName) ?? -1) >= 0) || false;
      if (memberOrRef &&JsonSchemaUtils.isReferenceObject(memberOrRef)) {

        throw new Error(`Unresolved reference in ${propertyName}`);
      }
      if (!memberOrRef) {
        return undefined;
      }
      member = memberOrRef;
    }
    return {
      parentName: parentName ?? "",
      propertyName: propertyName ?? "",
      schema: member,
      isRequired,
    };

  }

  /**
   * Checks if the given schema is defined in the current usecase: Exists and has any properties.
   * @param schemaDoc Schema document, typically Usecase.Schema
   * @param schemaNames One or several names of the schema to check whether they are defined.
   * If any schema is exists and has properties, returns true.
   */
   public static isDefined(schemaDoc: OpenAPIV3.Document, ...schemaNames: string[]): boolean {
    if (schemaNames.length === 1) {
      const schema: any = schemaDoc?.components?.schemas?.[schemaNames[0]];
      if (!schema) {
        return false;
      }
      return Object.keys(schema.properties).length > 0;
    }
    return !!schemaNames.find((x) => {
      return this.isDefined(schemaDoc, x);
    });
  }

  /** Typeguard for OpenAPIV2 */
  public static isOpenAPIV3(doc: OpenAPIV3.Document | OpenAPIV2.Document | IJsonSchema): doc is OpenAPIV3.Document {
    return (doc as OpenAPIV3.Document)?.openapi?.startsWith("3.") || false;
  }

  /** Typeguard for OpenAPIV3 */
  public static isOpenAPIV2(doc: OpenAPIV3.Document | OpenAPIV2.Document | IJsonSchema): doc is OpenAPIV2.Document {
    return (doc as OpenAPIV2.Document)?.swagger?.startsWith("2.") || false;
  }

  /** Typeguard for JSON schema (TODO: consider adding more generic Schema typing).) */
  public static isSchemaDocument(doc: OpenAPIV3.Document | OpenAPIV2.Document | IJsonSchema): doc is IJsonSchema {
    return !!(doc as IJsonSchema)?.$schema;
  }

  /** Typeguard for OpenAPIV3.ReferenceObject  */
  public static isReferenceObject(schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject): schema is OpenAPIV3.ReferenceObject {
    return !!(schema as OpenAPIV3.ReferenceObject)?.$ref;
  }

  /** Typeguard for OpenAPIV3.NonArraySchemaObject  */
  public static isNonArraySchemaObject(schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject): schema is OpenAPIV3.NonArraySchemaObject {
    return ["boolean", "object", "number", "string", "integer"].indexOf((schema as any).type) >= 0;
  }

  /** Typeguard for OpenAPIV3.ArraySchemaObject  */
  public static isArraySchemaObject(schema: OpenAPIV3.SchemaObject | OpenAPIV3.ReferenceObject): schema is OpenAPIV3.ArraySchemaObject {
    return (schema as OpenAPIV3.ArraySchemaObject).type === "array";
  }
}
